import {
  STORE_CART_DESIGN_ADD_OPTION,
  STORE_CART_DESIGN_NAV_QUERY_KEYS,
  STORE_CART_OPEN_QUERY_KEY,
  STORE_CART_PAGE_MIN_WIDTH,
  STORE_CART_PAGE_MAX_WIDTH,
  type StoreCartDesignAddOption,
} from '../storeCart.constants';
import { getStoreCartDesignGroupKey } from './storeCartDesignGroups.utils';
import type { ICartItemDetail, ICartItemPayload } from '@/common/interfaces/cart.interface';

export const STORE_CART_DESIGN_ADD_CONTEXT_STORAGE_KEY = 'storeCartDesignAddContext';

/**
 * Proof id for `/design/:id` — same id as DesignCard / DesignsList (`proof-details` API).
 * Do not use `designPricing.design_id` or cart `designId` (proof-item ids).
 */
export const getStoreCartDesignDetailsProofId = (
  designGroupItems: ICartItemDetail[],
  fallbackItem: ICartItemDetail
): number => {
  const itemWithProofId = designGroupItems.find((item) => item.proofId > 0) ?? fallbackItem;

  return itemWithProofId.proofId || 0;
};

export interface IStoreCartDesignNavContext {
  addOption: StoreCartDesignAddOption;
  styleCode?: string;
  proofId?: number;
}

export const parseStoreCartDesignNavParams = (
  searchParams: URLSearchParams
): IStoreCartDesignNavContext | null => {
  const addOption = searchParams.get(STORE_CART_DESIGN_NAV_QUERY_KEYS.ADD_OPTION);

  if (
    addOption !== STORE_CART_DESIGN_ADD_OPTION.COLOR &&
    addOption !== STORE_CART_DESIGN_ADD_OPTION.PRODUCT
  ) {
    return null;
  }

  const styleCode = searchParams.get(STORE_CART_DESIGN_NAV_QUERY_KEYS.STYLE_CODE)?.trim();

  return {
    addOption,
    ...(styleCode ? { styleCode } : {}),
  };
};

export const persistStoreCartDesignNavParams = (context: IStoreCartDesignNavContext): void => {
  sessionStorage.setItem(STORE_CART_DESIGN_ADD_CONTEXT_STORAGE_KEY, JSON.stringify(context));
};

export const readPersistedStoreCartDesignNavParams = (
  proofId?: number
): IStoreCartDesignNavContext | null => {
  try {
    const raw = sessionStorage.getItem(STORE_CART_DESIGN_ADD_CONTEXT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as IStoreCartDesignNavContext;

    if (
      parsed.addOption !== STORE_CART_DESIGN_ADD_OPTION.COLOR &&
      parsed.addOption !== STORE_CART_DESIGN_ADD_OPTION.PRODUCT
    ) {
      return null;
    }

    if (proofId && parsed.proofId && parsed.proofId !== proofId) {
      return null;
    }

    return {
      addOption: parsed.addOption,
      ...(parsed.styleCode ? { styleCode: parsed.styleCode } : {}),
    };
  } catch {
    return null;
  }
};

export const clearPersistedStoreCartDesignNavParams = (): void => {
  sessionStorage.removeItem(STORE_CART_DESIGN_ADD_CONTEXT_STORAGE_KEY);
};

export const resolveStoreCartDesignNavParams = (
  searchParams: URLSearchParams,
  proofId?: number
): IStoreCartDesignNavContext | null =>
  parseStoreCartDesignNavParams(searchParams) ?? readPersistedStoreCartDesignNavParams(proofId);

export const buildStoreCartDesignDetailsPath = (
  proofId: number,
  options: IStoreCartDesignNavContext
): string => {
  const params = new URLSearchParams({
    [STORE_CART_DESIGN_NAV_QUERY_KEYS.ADD_OPTION]: options.addOption,
  });

  if (options.styleCode) {
    params.set(STORE_CART_DESIGN_NAV_QUERY_KEYS.STYLE_CODE, options.styleCode);
  }

  return `/design/${proofId}?${params.toString()}`;
};

interface IFilterDesignGroupCartPayloadParams {
  storeCartDesignNav: IStoreCartDesignNavContext | null;
  selectedStyleCode: string;
  selectedDesignId: number;
}

const normalizeStyleCode = (styleCode: string): string => styleCode.trim().toUpperCase();

/**
 * When opened from store cart "+" actions, only add the single product-color line the user selected.
 * Never falls back to the full design group when scoped add context is present.
 */
export const filterDesignGroupCartPayloadForStoreCartAdd = (
  payloads: ICartItemPayload[],
  { storeCartDesignNav, selectedStyleCode, selectedDesignId }: IFilterDesignGroupCartPayloadParams
): ICartItemPayload[] => {
  if (!storeCartDesignNav) {
    return payloads;
  }

  if (!selectedStyleCode?.trim() || !selectedDesignId) {
    return [];
  }

  const styleCodeForColorAdd =
    storeCartDesignNav.addOption === STORE_CART_DESIGN_ADD_OPTION.COLOR
      ? storeCartDesignNav.styleCode || selectedStyleCode
      : selectedStyleCode;

  const normalizedTargetStyle = normalizeStyleCode(styleCodeForColorAdd);

  return payloads.filter(
    (item) =>
      normalizeStyleCode(item.apparelStyleCode) === normalizedTargetStyle &&
      Number(item.designId) === Number(selectedDesignId)
  );
};

export const clearStoreCartDesignNavQueryParams = (
  searchParams: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  next.delete(STORE_CART_DESIGN_NAV_QUERY_KEYS.ADD_OPTION);
  next.delete(STORE_CART_DESIGN_NAV_QUERY_KEYS.STYLE_CODE);
  return next;
};

export const isStoreCartOpenQueryParam = (searchParams: URLSearchParams): boolean =>
  searchParams.get(STORE_CART_OPEN_QUERY_KEY) === 'true';

export const STORE_CART_PAGE_PATH = '/store-cart';

export const STORE_CART_OPEN_URL_SYNC_PATHS = ['/', STORE_CART_PAGE_PATH] as const;

export const isStoreCartPagePath = (pathname: string): boolean => pathname === STORE_CART_PAGE_PATH;

export const isStoreCartPageViewport = (width: number): boolean =>
  width >= STORE_CART_PAGE_MIN_WIDTH && width <= STORE_CART_PAGE_MAX_WIDTH;

export const isStoreCartDrawerViewport = (width: number): boolean =>
  !isStoreCartPageViewport(width);

export interface IStoreCartOpenUrlResolution {
  redirectPath?: string;
  openOverlay: boolean;
}

export const buildStoreCheckoutUrl = (storeId: number): string =>
  `/store-checkout/new?storeId=${storeId}`;

export const STORE_CART_DESIGN_GROUP_MINIMUMS_STORAGE_KEY = 'storeCartDesignGroupMinimums';

export const persistStoreCartDesignGroupMinimums = (cartItems: ICartItemDetail[]): void => {
  const minimums: Record<string, number> = {};

  cartItems.forEach((item) => {
    const groupKey = getStoreCartDesignGroupKey(item);
    const selectedMinimum = Number(item.minQuant ?? item.quantity ?? 0);

    if (selectedMinimum > 0) {
      minimums[groupKey] = selectedMinimum;
    }
  });

  sessionStorage.setItem(STORE_CART_DESIGN_GROUP_MINIMUMS_STORAGE_KEY, JSON.stringify(minimums));
};

export const readPersistedStoreCartDesignGroupMinimums = (): Record<string, number> => {
  try {
    const raw = sessionStorage.getItem(STORE_CART_DESIGN_GROUP_MINIMUMS_STORAGE_KEY);

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as Record<string, number>;

    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([, value]) => typeof value === 'number' && Number.isFinite(value) && value > 0
      )
    );
  } catch {
    return {};
  }
};

export const clearPersistedStoreCartDesignGroupMinimums = (): void => {
  sessionStorage.removeItem(STORE_CART_DESIGN_GROUP_MINIMUMS_STORAGE_KEY);
};

export const buildStoreCartOpenUrl = (width: number): string =>
  isStoreCartPageViewport(width)
    ? `${STORE_CART_PAGE_PATH}?${STORE_CART_OPEN_QUERY_KEY}=true`
    : `/?${STORE_CART_OPEN_QUERY_KEY}=true`;

/**
 * Maps `storeCartOpen=true` to page UI (tablet, 621–992px) or drawer overlay
 * (mobile and desktop). Both surfaces use the `storeCartOpen=true` query param.
 */
export const resolveStoreCartOpenFromUrl = (
  pathname: string,
  width: number
): IStoreCartOpenUrlResolution => {
  if (isStoreCartPageViewport(width)) {
    return {
      openOverlay: false,
      ...(isStoreCartPagePath(pathname) ? {} : { redirectPath: buildStoreCartOpenUrl(width) }),
    };
  }

  if (isStoreCartPagePath(pathname)) {
    return {
      openOverlay: true,
      redirectPath: buildStoreCartOpenUrl(width),
    };
  }

  return { openOverlay: true };
};

export const shouldSyncStoreCartOpenUrl = (pathname: string): boolean =>
  STORE_CART_OPEN_URL_SYNC_PATHS.includes(
    pathname as (typeof STORE_CART_OPEN_URL_SYNC_PATHS)[number]
  );

export const syncStoreCartOpenQueryParam = (
  searchParams: URLSearchParams,
  isOpen: boolean
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);

  if (isOpen) {
    next.set(STORE_CART_OPEN_QUERY_KEY, 'true');
  } else {
    next.delete(STORE_CART_OPEN_QUERY_KEY);
  }

  return next;
};
